import { Check, AlertCircle, Mail, Eye } from 'lucide-react';
import SEO from '@/components/SEO';

/**
 * Shared presentational component for the keepsake selection page.
 * Used by both the public client page (ClientSelection) and the
 * authenticated ops preview page (ClientSelectionPreview).
 *
 * Props:
 *   data: payload from /api/select/{token} or /api/ops/leads/{id}/selection-preview
 *   selected: string[] currently selected item ids
 *   onToggle: (id) => void
 *   onSave: () => void | null (null disables the save bar)
 *   saving: boolean
 *   savedAt: ISO string | null
 *   error: string | null (in-flight save error message)
 *   previewMode: boolean — when true, shows banner + disables save UI
 */
export default function SelectionView({ data, selected, onToggle, onSave, saving, savedAt, error, previewMode = false }) {
  if (!data) return null;

  const optionalSelected = selected.filter(id => {
    const k = data.keepsakes.find(kk => kk.id === id);
    return k && !k.is_always_included;
  });

  const canSubmit = !previewMode && optionalSelected.length >= 2 && optionalSelected.length <= 4 && !data.is_locked;

  const always = data.keepsakes.filter(k => k.is_always_included);
  const optional = data.keepsakes.filter(k => !k.is_always_included);
  const deadline = data.selection_deadline ? new Date(data.selection_deadline).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : null;

  return (
    <div className="min-h-screen bg-[#FCFBF9]" data-testid="client-selection-page">
      <SEO title="Choose Your Keepsakes · Heirloom Stories Studio" />

      {/* Preview Banner */}
      {previewMode && (
        <div className="bg-amber-100 border-b border-amber-300 text-amber-900" data-testid="preview-banner">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 py-3 flex items-center justify-center gap-2 text-xs sm:text-sm font-medium tracking-wide">
            <Eye size={14} />
            <span className="uppercase tracking-widest text-[10px] sm:text-xs">Preview Mode</span>
            <span className="text-amber-700 font-normal">— this is what your client will see. Selections made here will not be saved.</span>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="border-b border-[#E7E5DF]">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-5">
          <h1 className="font-['Cormorant_Garamond'] text-lg font-semibold text-[#1C1917] tracking-tight">Heirloom Stories Studio</h1>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 sm:px-12 pt-20 pb-14 text-center">
        <p className="text-[11px] uppercase tracking-[0.25em] text-[#A89F91] mb-6">Your Heirloom Box</p>
        <h2 className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl font-medium text-[#1C1917] leading-tight mb-6">
          Hello {data.client_first_name || 'there'} —<br />
          choose the keepsakes for {data.loved_one_first_name ? `${data.loved_one_first_name}'s` : 'your'} box.
        </h2>
        <p className="text-base text-[#57534E] leading-relaxed max-w-xl mx-auto">
          These are the pieces we'll thoughtfully craft, print, and place into the Heirloom Box your family will open together.
          Choose <span className="text-[#1C1917] font-medium">two to four</span> keepsakes — and take your time. You can revisit this page any time before {deadline ? <span className="text-[#1C1917]">{deadline}</span> : 'your link expires'}.
        </p>
      </section>

      {/* Always Included */}
      {always.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 sm:px-12 pb-12">
          <div className="border-t border-[#E7E5DF] pt-10">
            <p className="text-[11px] uppercase tracking-[0.25em] text-[#A89F91] mb-2 text-center">Always included</p>
            <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] text-center mb-10">In every Heirloom Box</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {always.map(k => (
                <div key={k.id} data-testid={`always-item-${k.id}`} className="text-center">
                  {k.photo_url ? (
                    <div className="aspect-[4/5] w-full overflow-hidden bg-[#F2EFE9] mb-4">
                      <img src={k.photo_url} alt={k.name} className="w-full h-full object-cover" />
                    </div>
                  ) : (
                    <div className="aspect-[4/5] w-full bg-[#F2EFE9] mb-4" />
                  )}
                  <h4 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1C1917] mb-1">{k.name}</h4>
                  {k.description && <p className="text-sm text-[#78716C] leading-relaxed">{k.description}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Optional / Selectable */}
      <section className="max-w-5xl mx-auto px-6 sm:px-12 pb-16">
        <div className="border-t border-[#E7E5DF] pt-10">
          <p className="text-[11px] uppercase tracking-[0.25em] text-[#A89F91] mb-2 text-center">Choose 2 to 4</p>
          <h3 className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-medium text-[#1C1917] text-center mb-2">Your selections</h3>
          <p className="text-sm text-[#78716C] text-center mb-10">{optionalSelected.length} of 4 chosen</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {optional.map(k => {
              const isSel = selected.includes(k.id);
              const canSelect = isSel || optionalSelected.length < 4;
              const disabled = data.is_locked || (!canSelect && !isSel);
              return (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => onToggle(k.id)}
                  disabled={disabled}
                  data-testid={`keepsake-${k.id}`}
                  className={`text-left transition-all ${isSel ? 'ring-2 ring-[#1C1917] ring-offset-4 ring-offset-[#FCFBF9]' : 'opacity-95 hover:opacity-100'} ${!canSelect && !isSel ? 'opacity-40' : ''} ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                >
                  <div className="relative aspect-[4/5] w-full overflow-hidden bg-[#F2EFE9] mb-4">
                    {k.photo_url ? (
                      <img src={k.photo_url} alt={k.name} className="w-full h-full object-cover" />
                    ) : null}
                    {isSel && (
                      <div className="absolute top-3 right-3 w-7 h-7 bg-[#1C1917] text-[#FCFBF9] flex items-center justify-center">
                        <Check size={14} />
                      </div>
                    )}
                  </div>
                  <h4 className="font-['Cormorant_Garamond'] text-xl font-medium text-[#1C1917] mb-1">{k.name}</h4>
                  {k.description && <p className="text-sm text-[#78716C] leading-relaxed">{k.description}</p>}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sticky Confirm Bar */}
      {!data.is_locked && !previewMode && onSave && (
        <div className="sticky bottom-0 border-t border-[#E7E5DF] bg-[#FCFBF9]/95 backdrop-blur-sm">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 py-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-sm text-[#57534E] text-center sm:text-left">
              {optionalSelected.length < 2 && <span>Please choose at least 2 keepsakes to save.</span>}
              {optionalSelected.length >= 2 && optionalSelected.length <= 4 && (
                <span>{savedAt ? `Last saved ${new Date(savedAt).toLocaleString()}. ` : ''}You can keep updating until production begins.</span>
              )}
            </div>
            <button
              onClick={onSave}
              disabled={!canSubmit || saving}
              data-testid="save-selections-button"
              className="bg-[#1C1917] text-[#FCFBF9] hover:bg-[#322f2d] disabled:opacity-40 disabled:cursor-not-allowed px-8 py-3 text-sm tracking-wide font-medium transition-colors"
            >
              {saving ? 'Saving…' : savedAt ? 'Update Selections' : 'Save Selections'}
            </button>
          </div>
          {error && <p className="max-w-5xl mx-auto px-6 sm:px-12 pb-3 text-xs text-red-600">{error}</p>}
        </div>
      )}

      {/* Preview Mode Footer Bar */}
      {previewMode && (
        <div className="sticky bottom-0 border-t border-amber-300 bg-amber-50/95 backdrop-blur-sm" data-testid="preview-footer-bar">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 text-amber-900">
            <div className="text-xs sm:text-sm flex items-center gap-2">
              <Eye size={14} />
              <span>Preview only — selections won't be saved. Close this tab when you're done.</span>
            </div>
            <span className="text-xs uppercase tracking-widest font-semibold">{optionalSelected.length}/4 demo picks</span>
          </div>
        </div>
      )}

      {data.is_locked && (
        <div className="border-t border-[#E7E5DF] bg-[#F2EFE9]">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 py-6 text-center">
            <p className="text-sm text-[#1C1917]">Your selections are confirmed and your Heirloom Box is in production. We'll be in touch soon.</p>
          </div>
        </div>
      )}

      <footer className="border-t border-[#E7E5DF] mt-12">
        <div className="max-w-5xl mx-auto px-6 sm:px-12 py-8 text-center text-xs text-[#A89F91]">
          Heirloom Stories Studio · A keepsake for generations
        </div>
      </footer>
    </div>
  );
}

/** Error/invalid/expired views (also shared) */
export function SelectionInvalid() {
  return (
    <div className="min-h-screen bg-[#FCFBF9] flex flex-col items-center justify-center px-6">
      <SEO title="Selection Link Not Found · Heirloom Stories Studio" />
      <AlertCircle className="text-[#A89F91] mb-4" size={32} />
      <h1 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#1C1917] mb-2">This link isn't valid</h1>
      <p className="text-sm text-[#78716C] max-w-md text-center">If you received this link from your interviewer, please reach back out — we'll send you a fresh one right away.</p>
    </div>
  );
}

export function SelectionExpired({ interviewerName, interviewerEmail }) {
  return (
    <div className="min-h-screen bg-[#FCFBF9] flex flex-col items-center justify-center px-6">
      <SEO title="Selection Link Expired · Heirloom Stories Studio" />
      <AlertCircle className="text-[#A89F91] mb-4" size={32} />
      <h1 className="font-['Cormorant_Garamond'] text-3xl font-medium text-[#1C1917] mb-3">Your selection window has closed</h1>
      <p className="text-sm text-[#78716C] max-w-md text-center mb-4">No worries — please reach out and we'll send you a fresh link.</p>
      {interviewerEmail && (
        <a href={`mailto:${interviewerEmail}`} className="flex items-center gap-2 text-sm text-[#1C1917] border border-[#1C1917] px-5 py-2.5 hover:bg-[#1C1917] hover:text-[#FCFBF9] transition-colors" data-testid="contact-interviewer-link">
          <Mail size={14} /> Contact {interviewerName || 'your interviewer'}
        </a>
      )}
    </div>
  );
}
